import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputOtp } from 'primeng/inputotp';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
	afterNextRender,
	ChangeDetectionStrategy,
	Component,
	effect,
	ElementRef,
	inject,
	Injector,
	input,
	output,
	ViewChild
} from '@angular/core';

import { KioskPinForm } from '../../models/kiosk-auth-forms.interfaces';
import { pinValidator } from '../../../../shared/validators/pin.validator';
import { USER_PIN_LENGTH } from '../../../../shared/models/user/user.constants';

@Component({
	selector: 'app-kiosk-pin-dialog',
	imports: [Dialog, Button, InputOtp, ReactiveFormsModule],
	templateUrl: './kiosk-pin-dialog.component.html',
	styleUrl: './kiosk-pin-dialog.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class KioskPinDialogComponent {
	public readonly visible = input.required<boolean>();
	public readonly header = input.required<string>();
	public readonly description = input.required<string>();
	public readonly confirmLabel = input<string>('Continue');
	public readonly isSubmitting = input<boolean>(false);

	public readonly pinConfirmed = output<string>();
	public readonly visibleChange = output<boolean>();

	public readonly USER_PIN_LENGTH = USER_PIN_LENGTH;

	@ViewChild('pinInputHost')
	private pinInputHost?: ElementRef<HTMLElement>;

	private readonly fb = inject(FormBuilder);
	private readonly injector = inject(Injector);

	public pinForm: FormGroup<KioskPinForm> = this.fb.nonNullable.group({
		pin: this.fb.nonNullable.control('', [Validators.required, pinValidator()])
	});

	constructor() {
		effect(() => {
			if (!this.visible()) {
				this.pinForm.reset();
				return;
			}

			this.scheduleFirstPinInputFocus();
		});
	}

	/**
	 * Clears the PIN control and moves focus to the first OTP cell.
	 *
	 * @return {void}
	 */
	public resetPinAndFocus(): void {
		this.pinForm.reset();
		this.pinForm.markAsPristine();
		this.pinForm.markAsUntouched();
		this.scheduleFirstPinInputFocus();
	}

	/**
	 * Submits the PIN when Enter is pressed inside the OTP inputs.
	 * InputOtp swallows Enter internally, so the event is handled on the form level.
	 *
	 * @param {Event} event - Keyboard event from the PIN form.
	 * @return {void}
	 */
	public onEnterKey(event: Event): void {
		if (this.isSubmitting()) {
			return;
		}

		event.preventDefault();
		this.onConfirm();
	}

	/**
	 * Validates the PIN form and emits the entered PIN.
	 *
	 * @return {void}
	 */
	public onConfirm(): void {
		if (this.isSubmitting()) {
			return;
		}

		if (this.pinForm.invalid) {
			this.pinForm.markAllAsTouched();
			return;
		}

		this.pinConfirmed.emit(this.pinForm.getRawValue().pin);
	}

	/**
	 * Focuses the first OTP input after the dialog content is rendered.
	 *
	 * @return {void}
	 */
	private scheduleFirstPinInputFocus(): void {
		afterNextRender(() => this.focusFirstPinInput(), { injector: this.injector });
	}

	/**
	 * Moves keyboard focus to the first PIN input cell.
	 *
	 * @return {void}
	 */
	private focusFirstPinInput(): void {
		const firstPinInput = this.pinInputHost?.nativeElement.querySelector('input');
		firstPinInput?.focus();
	}
}
